import React, { Component } from "react";
import { isEmpty, isEqual, cloneDeep, get, pick } from "lodash";
import classnames from "classnames";
import PropTypes from "prop-types";
import { createContext } from "@ali/render-engine";
import Components, { getRemoteModule } from "../Remote";
import SubmitManager from "../../addons/submit";
import ResolverManager from "../../addons/resolvers";
import ScrollManager from "../../addons/scroller";
import ErrorManager from "../../addons/error-tab";
import StepManager from "../../addons/steps";
import KamlaReport from "../../addons/kamla-report";
import getFieldApi from "./fieldApi";
import { createNewEvent } from "../../utils/index";

const filterRealChild = (children, props) => {
  // 过滤收集所有 'AutoColumnsLayout'、'Column'、'ColumnsLayout' 的子组件
  const realChildren = [];
  if (!isEmpty(children)) {
    children.forEach((item) => {
      if (
        ["AutoColumnsLayout", "Column", "ColumnsLayout"].includes(
          item.componentName
        )
      ) {
        realChildren.push(...filterRealChild(item.children));
      } else {
        realChildren.push(item);
      }
    });
  }

  // 若GroupLayout下只含有非提交类型的组件，且开启了forceHide属性，则过滤
  if (
    props &&
    props.forceHide &&
    !realChildren.find(
      (i) =>
        !["Block", "Button", "Label", "LineBreak", "Message"].includes(
          i.componentName
        )
    )
  ) {
    return [];
  }

  return realChildren;
};

class Form extends Component {
  constructor(props) {
    super(props);
    this.eventBinders = []; // 待绑定的事件列表
    this.validationMap = {}; // 字段校验规则Map
    this.schema = null;
    this.formContext = null;
    this.fieldApi = null;
    this.submitManager = null;
    this.resolverManager = null;
    this.scrollManager = null;
    this.StepManager = null;
    this.errorManager = null;

    this.onFieldsSelectChange = this.onFieldsSelectChange.bind(this);
  }

  componentDidMount() {
    const { onFormReady, detail, params, formOptions } = this.props;

    const global = { detail, params, formOptions };
    this.setSchema();
    this.setContext(global);
    this.setComponents().then(() => {
      this.setFieldApi();
      this.setScrollManager();

      const mode = get(this.schema, "pages[0].layout.props.mode", "normal");

      if (mode === "step" && !detail) {
        this.setStepManager();
      }

      this.setErrorManager();
      this.setSubmitManager();
      this.setResolverManager();
      this.setKamlaReport();
      this.bindEvents();
      this.formContext.render();
      onFormReady(this);
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { schema: prevSchema, formOptions: prevFormOptions = {} } = prevProps;
    const {
      schema,
      onFormReady,
      detail,
      params,
      formOptions = {},
    } = this.props;
    const global = { detail, params, formOptions };

    const isDiffFieldsSelectMode =
      !isEqual(
        prevFormOptions.fieldsSelectModeStatus,
        formOptions.fieldsSelectModeStatus
      ) ||
      !isEqual(
        prevFormOptions.fieldsSelectModeData,
        formOptions.fieldsSelectModeData
      );

    if (!isEqual(prevSchema, schema)) {
      this.eventBinders = [];
      this.validationMap = {};
      this.formContext.destroyPage();
      setTimeout(() => {
        this.setSchema(global);
        this.setContext(global);
        this.setComponents().then(() => {
          this.setFieldApi();
          this.setScrollManager();
          const mode = get(this.schema, "pages[0].layout.props.mode", "step");

          if (mode === "step" && !detail) {
            this.setStepManager();
          }

          this.setErrorManager();
          this.setSubmitManager();
          this.setResolverManager();
          this.bindEvents();
          this.formContext.render();
          onFormReady(this);
        });
      }, 10);
    } else if (isDiffFieldsSelectMode) {
      this.eventBinders = [];
      this.validationMap = {};

      // 解决EventEmitter memory 监听过多问题
      this.formContext.emitter.removeAllListeners();

      this.setSchema(global);
      this.setContext(global);
      this.setComponents().then(() => {
        this.setFieldApi();
        this.setScrollManager();
        const mode = get(this.schema, "pages[0].layout.props.mode", "step");

        if (mode === "step" && !detail) {
          this.setStepManager();
        }

        this.setErrorManager();
        this.setSubmitManager();
        this.setResolverManager();
        this.bindEvents();

        this.formContext.reRender({
          schema: cloneDeep(this.schema),
          destroyable: true,
        });

        onFormReady(this);
      });
    }
  }

  setSchema() {
    const { schema, detail, params, formOptions } = this.props;
    const global = { detail, params, formOptions };
    const { instanceData, pages = [], ...rest } = schema;
    const realPages = pages.map((page) => ({
      ...page,
      layout: this.transformComponent(page.layout, global),
    }));
    // TODO 处理实例数据（数据转换逻辑）
    // if (instanceData && instanceData.formData) {
    // }
    this.schema = {
      ...rest,
      pages: realPages,
      instanceData,
    };
  }

  setContext(formProps = {}) {
    this.formContext = createContext({
      id: "GovForm",
      schema: this.schema,
      locale: "zh_CN",
      whetherComponentValidation: true,
      i18nTransformWithRaw: true,
      isSectionalRender: false,
      originSchema: cloneDeep(this.schema),
      globalProps: { ...formProps },
    });

    this.formContext.setGlobal({ ...(formProps.formOptions || {}) });

    window.__ctx__ = this.formContext;
  }

  setComponents() {
    const { getResourceUrl, params } = this.props;

    const obj = pick(params, "draftCompType");
    // 测试环境加载测试环境的自定义组件，比如文件上传组件，方便测试环境调试新功能
    if (!obj?.draftCompType && window.location.hostname === 'pre-recept.zjzwfw.gov.cn') {
      obj.draftCompType = 'all'
    }

    return getRemoteModule(getResourceUrl, obj)
      .then((isvModule) => {
        const mergeComp = [...Components, ...isvModule];

        this.formContext.setComponents(mergeComp);
      })
      .catch((err) => {
        console.log("加载组件异常", err);
        this.formContext.setComponents(Components);
      });
  }

  setFieldApi() {
    const { endpoint } = this.props;
    this.fieldApi = getFieldApi({
      context: this.formContext,
      endpoint,
      validationMap: this.validationMap,
    });
  }

  setSubmitManager() {
    const { submitUrl, params, endpoint } = this.props;
    this.submitManager = new SubmitManager({
      fieldApi: this.fieldApi,
      context: this.formContext,
      url: submitUrl,
      params,
      endpoint,
      scrollManager: this.scrollManager,
      errorManager: this.errorManager,
    });
  }

  setResolverManager() {
    const { detail, isEdit, params, disableDevicePlugin } = this.props;
    this.resolverManager = new ResolverManager({
      fieldApi: this.fieldApi,
      context: this.formContext,
      schema: this.schema,
      isDetail: detail,
      isEdit,
      disableDevicePlugin,
      params,
      updateRequestCounter: this.submitManager
        ? this.submitManager.updateRequestCounter
        : () => { },
    });
    if (detail) {
      // 增加详情页中异步请求联动，更新表单项异步数据用于渲染，例如SearchInput异步数据回写dataSource.options
      // 与服务端@十景沟通，办件详情页触发异步接口会导致数据问题，因此去掉异步请求
      this.resolverManager.parse(["rules", "iframeRequest"]);
    } else {
      this.resolverManager.parse();
    }

    this.formContext.setGlobalEvents(this.resolverManager.toProps());
  }

  setKamlaReport() {
    KamlaReport({ fieldApi: this.fieldApi, context: this.formContext });
  }

  setScrollManager() {
    this.scrollManager = new ScrollManager({
      fieldApi: this.fieldApi,
      context: this.formContext,
      schema: this.schema,
    });
  }

  setStepManager() {
    this.stepManager = new StepManager({
      fieldApi: this.fieldApi,
      context: this.formContext,
      schema: this.schema,
      onChange: (pageIds) => {
        this.formContext.emitter.emit("pageChange", pageIds);

        // lock 表格时，在异步更新props.$state时，需要触发window.resize来触发 adjustSize重新计算表格bodyHeight，从而避免锁定列的maxHeight计算不准导致内容展示不全
        // 详见@alifd/next table 源码：https://github.com/alibaba-fusion/next/blob/master/src/table/lock.jsx#L60
        window.dispatchEvent(createNewEvent("resize"));
      },
    });
  }

  setErrorManager() {
    this.errorManager = new ErrorManager({
      fieldApi: this.fieldApi,
      context: this.formContext,
      schema: this.schema,
    });
  }

  // 根据 fieldId 及 schema.instanceData 获取对应的表单项数据
  // return schema.instanceData.formData[i].fieldData.value
  getFieldValue(fieldId) {
    const { schema: { instanceData: { formData = [] } = {} } = {} } =
      this.props;
    const formField = formData.find((item) => item.fieldId === fieldId);
    return ((formField && formField.fieldData) || {}).value;
  }

  getFormData(options = {}) {
    return this.fieldApi.getFieldsData({
      disableValid: true,
      scroll: false,
      ...options,
    });
  }

  // 表单示例渲染页面ready后的操作
  bindEvents() {
    const { initErrorMsg } = this.props;

    this.formContext.onPageReady(() => {
      this.eventBinders.forEach((binder) => {
        binder();
      });

      this.stepManager && this.stepManager.go(0);
      this.scrollManager.reportGroups();
      this.resolverManager.apply();
      initErrorMsg && this.errorManager.updateFormErrors();
    });
  }

  rebindHandler() {
    return this.resolverManager.rebind();
  }

  // 遍历 schema 树形结构，为每个节点获取instanceData中的fieldValue值及绑定节点数据和方法
  transformComponent(schema, global) {
    if (!schema) return;
    const { detail } = this.props;
    schema = cloneDeep(schema);
    const { id, componentName, props, children } = schema;

    // 分组组件下没有有效的子组件，滤除
    if (
      componentName === "GroupLayout" &&
      isEmpty(filterRealChild(children, props))
    ) {
      return;
    }
    // 编辑态设置只读，修改为 disabled
    if (detail) {
      props.readonly = true;
      props.isInDetail = true;
    } else if (!detail && props.readonly) {
      delete props.readonly;
      props.disabled = true;
    }
    // 增加空规则，确保能复原校验状态
    if (isEmpty(props.validation)) {
      props.validation = [
        {
          type: "emptyRule",
        },
      ];
    }
    // 处理字段的默认值
    const value = this.getFieldValue(props.fieldId);
    switch (componentName) {
      case "Input":
      case "Textarea":
        if (value && value.indexOf("*") !== -1) {
          this.validationMap[props.fieldId] = props.validation || [];
          // 仅保留必填校验和空规则
          props.validation = (props.validation || []).filter(
            (v) => v.type === "required" || v.type === "emptyRule"
          );
          this.eventBinders.push(() => {
            this.fieldApi.onValueChange(
              props.fieldId,
              ({ value: val } = {}) => {
                if (!val || val.indexOf("*") === -1) {
                  this.fieldApi.setValidation(
                    props.fieldId,
                    this.validationMap[props.fieldId],
                    false,
                    true
                  );
                }
              },
              { ignoreError: true }
            );
          });
        }
        break;
      default:
        break;
    }

    // 为每个组件注入表单项模式选择onchange回调方法
    props.onFieldsSelectChange = this.onFieldsSelectChange;

    props.onRebindEvents = this.rebindHandler.bind(this);
    props.getRelaFormData = this.getFormData.bind(this);
    props.global = global;
    this.eventBinders.push(() => {
      this.scrollManager.bindErrorMsgChangeHandler(props.fieldId);
      this.errorManager.bindErrorMsgChangeHandler(props.fieldId);
    });
    const newSchema = {
      ...schema,
      id: id.split(".").join("_"),
      props,
    };
    if (children) {
      newSchema.children = children
        .map((child) => this.transformComponent(child, global))
        .filter((child) => !!child);
    }
    return newSchema;
  }

  onFieldsSelectChange(params) {
    // 重新包装props.onFieldsSelectChange，解决hooks对象传方法时的闭包问题
    typeof this.props.onFieldsSelectChange === "function" &&
      this.props.onFieldsSelectChange(params);
  }

  render() {
    const { detail } = this.props;
    return (
      <React.Fragment>
        <div
          id="GovForm"
          className={classnames({ "gov-form-detail": detail })}
        />
      </React.Fragment>
    );
  }
}

Form.propTypes = {
  schema: PropTypes.object,
  detail: PropTypes.bool,
  endpoint: PropTypes.string,
  onFormReady: PropTypes.func,
  url: PropTypes.string,
  params: PropTypes.object,
  getResourceUrl: PropTypes.string,
  onFieldsSelectChange: PropTypes.func,
};

Form.defaultProps = {
  schema: {},
  detail: false,
  endpoint: "pc",
  url: "/api/reception/execute",
  getResourceUrl: "/form/renderCompResource",
  onFormReady: () => { },
  params: {},
};

export default Form;
